import { Check } from '@/shared/icons/phosphor'
import { type JSX, Show, splitProps } from 'solid-js'

export type ChoiceCardLayout = 'row' | 'tile'

export interface ChoiceCardProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  label: JSX.Element
  hint?: string
  layout?: ChoiceCardLayout
}

const layoutClasses: Record<ChoiceCardLayout, string> = {
  row: 'flex w-full min-h-14 items-center gap-3 px-4 py-4 text-left has-checked:bg-[var(--color-primary-50)]',
  tile: 'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 px-3 py-2.5 text-center font-[var(--font-semibold)] tabular-nums has-checked:bg-[var(--color-primary-500)] has-checked:text-[color:var(--color-on-primary)]',
}

export function ChoiceCard(props: ChoiceCardProps) {
  const [local, rest] = splitProps(props, ['label', 'hint', 'layout', 'class'])
  const layout = () => local.layout ?? 'row'

  return (
    <label
      data-choice-card
      data-layout={layout()}
      class={`
        ds-choice relative cursor-pointer select-none
        rounded-[var(--radius-12)] border border-[var(--color-field-border)]
        bg-[var(--color-surface)] text-[color:var(--color-text)]
        text-[length:var(--text-base)] leading-[var(--leading-snug)]
        transition-[border-color,background-color] duration-[var(--duration-fast)]
        hover:not-has-checked:not-has-disabled:border-[var(--color-field-hover)]
        has-checked:border-[var(--color-primary-500)]
        has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-[var(--color-primary-500)]
        has-disabled:cursor-not-allowed has-disabled:opacity-50
        ${layoutClasses[layout()]}
        ${local.class ?? ''}
      `.trim()}
    >
      <input {...rest} type="radio" class="peer sr-only" />
      <Show when={local.hint}>
        <span
          data-choice-key
          aria-hidden="true"
          class="
            inline-flex h-7 w-7 shrink-0 items-center justify-center
            rounded-[var(--radius-8)] border border-[var(--color-field-border)]
            bg-[var(--color-surface)] text-[color:var(--color-text-muted)]
            text-[length:var(--text-xs)] font-[var(--font-semibold)]
            transition-colors duration-[var(--duration-fast)]
            peer-checked:border-[var(--color-primary-500)] peer-checked:bg-[var(--color-primary-500)]
            peer-checked:text-[color:var(--color-on-primary)]
          "
        >
          {local.hint}
        </span>
      </Show>
      <span class={layout() === 'row' ? 'min-w-0 flex-1' : undefined}>{local.label}</span>
      <Show when={layout() === 'row'}>
        <span
          data-choice-check
          aria-hidden="true"
          class="ds-choice-indicator rounded-full shrink-0"
        >
          <Check class="h-3 w-3" weight="bold" />
        </span>
      </Show>
    </label>
  )
}
